import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageOff } from 'lucide-react';

// Images splash par catégorie de plats
const SPLASH_IMAGES = {
  // Africain
  african: 'https://images.unsplash.com/photo-1604329760661-e71dc83f8f26?w=600&h=400&fit=crop&q=80',
  // Européen
  european: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=600&h=400&fit=crop&q=80',
  // Asiatique
  asian: 'https://images.unsplash.com/photo-1569718212165-3a8278d5f624?w=600&h=400&fit=crop&q=80',
  // Fast-Food
  fastfood: 'https://images.unsplash.com/photo-1561758033-d89a9ad46330?w=600&h=400&fit=crop&q=80',
  burger: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=600&h=400&fit=crop&q=80',
  pizza: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=600&h=400&fit=crop&q=80',
  // Végétarien
  vegetarian: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=600&h=400&fit=crop&q=80',
  salad: 'https://images.unsplash.com/photo-1546793665-c74683f339c1?w=600&h=400&fit=crop&q=80',
  // Desserts
  dessert: 'https://images.unsplash.com/photo-1551024506-0bccd828d307?w=600&h=400&fit=crop&q=80',
  cake: 'https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=600&h=400&fit=crop&q=80',
  icecream: 'https://images.unsplash.com/photo-1563805042-7684c019e1cb?w=600&h=400&fit=crop&q=80',
  // Boissons
  beverage: 'https://images.unsplash.com/photo-1544145945-f90425340c7e?w=600&h=400&fit=crop&q=80',
  coffee: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=600&h=400&fit=crop&q=80',
  juice: 'https://images.unsplash.com/photo-1600271886742-f049cd451bba?w=600&h=400&fit=crop&q=80',
  // Viandes
  meat: 'https://images.unsplash.com/photo-1529692236671-f1f6cf9683ba?w=600&h=400&fit=crop&q=80',
  chicken: 'https://images.unsplash.com/photo-1598103442097-8b74394b95c6?w=600&h=400&fit=crop&q=80',
  steak: 'https://images.unsplash.com/photo-1588168333986-5078d3ae3976?w=600&h=400&fit=crop&q=80',
  // Fruits de mer
  seafood: 'https://images.unsplash.com/photo-1559737558-2f5a77f0c60e?w=600&h=400&fit=crop&q=80',
  sushi: 'https://images.unsplash.com/photo-1579584425555-c3ce17fd4351?w=600&h=400&fit=crop&q=80',
  // Pâtes
  pasta: 'https://images.unsplash.com/photo-1621996346565-e3dbc646d9a9?w=600&h=400&fit=crop&q=80',
  // Soupe
  soup: 'https://images.unsplash.com/photo-1547592166-23ac45744acd?w=600&h=400&fit=crop&q=80',
  // Par défaut
  default: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=600&h=400&fit=crop&q=80'
};


const DishImageSplash = ({
  src,
  alt,
  category = null,
  dishName = '',
  className = '',
  showLoader = true
}) => {
  const { t } = useTranslation();
  const resolvedAlt = alt ?? t('dishImageSplash.defaultAlt');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [useSplash, setUseSplash] = useState(!src);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [fallbackApplied, setFallbackApplied] = useState(false);

  useEffect(() => {
    setCurrentSrc(src);
    setFallbackApplied(false);
    setImageError(false);
    setImageLoaded(false);
    setUseSplash(!src);
  }, [src]);

  // Déterminer la catégorie basée sur le nom du plat ou la catégorie
  const getCategoryKey = () => {
    const categoryName = typeof category === 'string' ? category : category?.name;
    if (categoryName) {
      const categoryLower = categoryName.toLowerCase();
      if (SPLASH_IMAGES[categoryLower]) return categoryLower;
    }

    // Recherche par mots-clés dans le nom du plat
    const nameLower = dishName?.toLowerCase() || resolvedAlt.toLowerCase();
    
    const keywords = {
      burger: ['burger', 'hamburger'],
      pizza: ['pizza'],
      sushi: ['sushi', 'sashimi', 'maki'],
      pasta: ['pasta', 'spaghetti', 'pâtes', 'lasagne', 'ravioli'],
      salad: ['salade', 'salad'],
      chicken: ['poulet', 'chicken'],
      steak: ['steak', 'boeuf', 'beef'],
      seafood: ['poisson', 'fish', 'crevettes', 'shrimp', 'fruits de mer'],
      soup: ['soupe', 'soup', 'bouillon'],
      dessert: ['dessert', 'gâteau', 'cake', 'tarte'],
      icecream: ['glace', 'ice cream', 'sorbet'],
      coffee: ['café', 'coffee', 'cappuccino', 'espresso'],
      juice: ['jus', 'juice', 'smoothie']
    };

    for (const [key, words] of Object.entries(keywords)) {
      if (words.some(word => nameLower.includes(word))) {
        return key;
      }
    }

    return 'default';
  };

  const handleImageError = () => {
    if (!fallbackApplied && /\.webp(\?.*)?$/i.test(currentSrc || '')) {
      setCurrentSrc((currentSrc || '').replace(/\.webp(\?.*)?$/i, '.jpg$1'));
      setFallbackApplied(true);
      return;
    }
    setImageError(true);
    setUseSplash(true);
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const categoryKey = getCategoryKey();
  const splashImage = SPLASH_IMAGES[categoryKey];

  // Si pas d'image source ou erreur, afficher l'image splash
  if (useSplash || imageError) {
    return (
      <div className={`relative overflow-hidden ${className}`}>
        <img
          src={splashImage}
          alt={resolvedAlt}
          className="w-full h-full object-cover transition-all duration-300 hover:scale-110"
          loading="lazy"
        />
        <div 
          className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-60"
        ></div>
        
        {/* Badge "Photo par défaut" */}
        <div className="absolute bottom-2 right-2 bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
          <ImageOff className="w-3 h-3" />
          {t('dishImageSplash.defaultImageBadge')}
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {/* Skeleton de chargement */}
      {!imageLoaded && showLoader && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse z-10">
          <div className="w-full h-full bg-gradient-to-br from-gray-200 via-gray-300 to-gray-200"></div>
        </div>
      )}

      {/* Image principale */}
      <img
        src={currentSrc}
        alt={resolvedAlt}
        className={`w-full h-full object-cover transition-all duration-300 hover:scale-110 ${
          imageLoaded ? 'opacity-100 z-20' : 'opacity-0 z-0'
        }`}
        onLoad={handleImageLoad}
        onError={handleImageError}
        loading="lazy"
      />

      {/* Overlay subtil au survol */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
    </div>
  );
};

export default DishImageSplash;
